import type { ScanPointGeometry } from "@/api/scan-geometry";
import { isViewablePayloadUrl } from "@/api/payloads";
import { formatForPayloadName, type StructureFormat } from "@/components/viz/molecule-structure";

/**
 * A torsion scan as ONE trajectory rather than N unrelated snapshots
 * (flowstack#1327).
 *
 * ## What AFFDO does, read from source
 *
 * AFFDO's scan output is a multi-frame object end to end. xtb's own scan log
 * *is* the trajectory — `mv xtbscan.log scan-final.xyz`
 * [`AFFDO@efb38ddc:src/xtbmod.py:592`] — and the extractor pulls "per-point XYZ
 * & energies from scan-final.xyz" out of that one file
 * [`xtbmod.py:746`]. The fitter states the invariant outright, refusing a
 * torsion whose frame count and angle count disagree:
 * `f"frames vs data mismatch for {dh}: xyz={len(blocks)} dat={len(ang)}"`
 * [`AFFDO@efb38ddc:src/ffpoptmod.py:437`]. **Frame index and scan-angle index
 * are the same ordinate.**
 *
 * The report's viewer then re-assembles that trajectory from the per-point
 * files it wrote — `GenerateViewerHtml`
 * [`AFFDO@efb38ddc:src/statsmod.py:4622`] globs `dh_{torsion}_NNN.xyz`, reads
 * every one [`statsmod.py:4677`], and embeds them as a JavaScript array of
 * frame strings [`statsmod.py:4687`] BEFORE the viewer exists
 * [`statsmod.py:4750`] — then steps with `viewer.removeAllModels();`
 * [`statsmod.py:4771`] `viewer.addModel(frames[currentFrame], "xyz");`
 * [`statsmod.py:4772`].
 *
 * **So the per-point storage is not the divergence.** AFFDO stores per point
 * too; what it does not do is fetch, parse and re-mount per point at VIEW time.
 * That is the shape this module ports: resolve the whole scan's frames up
 * front, then move an index.
 *
 * ## What this module is, and is not
 *
 * Pure. It decides, for each scan point, whether there is an address worth
 * fetching and — once the bytes are in hand — which frames the trajectory
 * actually has. It does no fetching and mounts nothing, so the join can be
 * pinned by a test with no DOM and no network.
 *
 * **A point without a frame keeps its own state; it is never skipped over.**
 * `frameIndexByPoint` is index-aligned with the points it was built from and
 * holds `null` exactly where the trajectory has nothing, so the caller renders
 * that point's specific reason rather than the nearest frame that did load.
 * Substituting a neighbour is the silent-wrong-render the whole panel exists to
 * refuse.
 */

/**
 * Why a point has no address to fetch — decided from the RECORD alone, before
 * any bytes.
 *
 * Deliberately a subset of `ScanGeometryPanel`'s `StructureMiss`, spelled
 * identically, so the panel maps it through with no translation table: these
 * are the misses a pure function can decide, and the fetch-outcome ones
 * (`unavailable`, `too_large`, `load_failed`, …) are not among them.
 */
export type ScanGeometryPlanMiss =
  | "not_recorded"
  | "unresolvable"
  | "unaddressed"
  | "unparsable"
  | "unsafe_address";

/** What to do about one scan point's geometry. */
export type ScanPointPlan =
  | {
      kind: "fetch";
      downloadUrl: string;
      format: StructureFormat;
      payloadName: string;
    }
  | { kind: "miss"; miss: ScanGeometryPlanMiss; payloadName: string | null };

/**
 * Decide one point's plan.
 *
 * The order of the checks is the order the states were separated in
 * flowstack#1202 and must not be reordered: the RECORD's own answer
 * (`not_recorded` / a status this release cannot resolve) comes before
 * anything about the address, because "there is no geometry" and "there is one
 * I cannot use" are different facts about different actors.
 */
export function planScanPoint(point: ScanPointGeometry): ScanPointPlan {
  if (point.geometry_status === "not_recorded") {
    return { kind: "miss", miss: "not_recorded", payloadName: null };
  }
  if (point.geometry_status !== "addressable") {
    // `unresolvable`, or a status this release does not know. Both mean the
    // same to a reader — there is an entry and this API cannot turn it into an
    // address — and that is a DIFFERENT fact from "there is no geometry".
    return { kind: "miss", miss: "unresolvable", payloadName: null };
  }
  const ref = point.geometry;
  if (ref === null) {
    return { kind: "miss", miss: "unaddressed", payloadName: null };
  }
  const format = formatForPayloadName(ref.payload_name);
  if (format === null) {
    return { kind: "miss", miss: "unparsable", payloadName: ref.payload_name };
  }
  // `download_url` is server-response data about to be fetched WITH THE USER'S
  // credentials, so it is pinned to the API's own payload-route shape before
  // the fetch rather than after it. `apiFetchPayloadText` enforces the same
  // rule at the boundary and is what actually protects the request; asking here
  // as well is what lets a refused address render as its own state instead of
  // as a generic load failure.
  if (!isViewablePayloadUrl(ref.download_url)) {
    return { kind: "miss", miss: "unsafe_address", payloadName: ref.payload_name };
  }
  return {
    kind: "fetch",
    downloadUrl: ref.download_url,
    format,
    payloadName: ref.payload_name,
  };
}

/** One loaded frame of the trajectory. */
export interface ScanFrame {
  /** Position in the SCAN's own grid — the ordinate AFFDO's invariant shares. */
  pointIndex: number;
  text: string;
  format: StructureFormat;
}

export interface ScanTrajectory {
  frames: readonly ScanFrame[];
  /**
   * Index into {@link frames} for each scan point, `null` where the trajectory
   * has no frame. Index-aligned with the `plans` it was built from.
   */
  frameIndexByPoint: readonly (number | null)[];
}

/**
 * Assemble the trajectory from the plans and whatever text arrived.
 *
 * `textFor` answers "what did this address load?" — `undefined` for an address
 * that has not arrived, failed, or came back blank. It is a lookup rather than
 * an array so the caller's fetch layer may dedupe two points that name the same
 * payload without the join having to know that it did.
 *
 * Empty text is treated as NO frame, not as an empty molecule: 3Dmol accepts
 * content it cannot make sense of and yields a model with no atoms, which
 * renders as a blank canvas indistinguishable from a working viewer aimed at
 * nothing.
 */
export function buildScanTrajectory(
  plans: readonly ScanPointPlan[],
  textFor: (downloadUrl: string) => string | undefined
): ScanTrajectory {
  const frames: ScanFrame[] = [];
  const frameIndexByPoint: (number | null)[] = [];
  plans.forEach((plan, pointIndex) => {
    if (plan.kind !== "fetch") {
      frameIndexByPoint.push(null);
      return;
    }
    const text = textFor(plan.downloadUrl);
    if (text === undefined || text.trim() === "") {
      frameIndexByPoint.push(null);
      return;
    }
    frameIndexByPoint.push(frames.length);
    frames.push({ pointIndex, text, format: plan.format });
  });
  return { frames, frameIndexByPoint };
}

/**
 * The distinct addresses this trajectory needs, in scan order and deduped.
 *
 * Deduped because two points naming one payload must cost one request, and
 * because the fetch layer keys on the URL — asking twice for the same key is
 * the same query.
 */
export function trajectoryFetchUrls(plans: readonly ScanPointPlan[]): readonly string[] {
  const seen = new Set<string>();
  for (const plan of plans) {
    if (plan.kind === "fetch") seen.add(plan.downloadUrl);
  }
  return [...seen];
}
